/**
 * NutritionProfileEditorDialog — Phase 2.2B.1 (Profile Editor)
 *
 * Endpoint: PUT /api/nutrition/profile/{athlete_id}
 * Body: { allergies, intolerances, dietary_preferences, nutrition_notes, medical_flags }
 *
 * Backend enforces write access via assert_can_write_athlete (ADMIN, NUTRITIONIST,
 * SNC, PHYSIO for assigned teams). UI gates the open-button on ADMIN/NUTRITIONIST
 * per Phase 2.2B spec.
 */
import React, { useEffect, useState } from 'react';
import { X, Plus, Loader2, Save } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const ChipList = ({ label, values, onChange, placeholder, testid, colorClass = 'bg-zinc-700/50 text-zinc-200 border-zinc-600' }) => {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (values.includes(v)) { setDraft(''); return; }
    onChange([...values, v]);
    setDraft('');
  };
  const remove = (idx) => onChange(values.filter((_, i) => i !== idx));
  return (
    <div data-testid={testid}>
      <label className="text-xs uppercase tracking-wider text-zinc-400 mb-1.5 block">{label}</label>
      <div className="flex flex-wrap gap-1.5 mb-2 min-h-[28px]">
        {values.length === 0 ? (
          <span className="text-zinc-600 text-xs italic">—</span>
        ) : values.map((v, i) => (
          <Badge key={i} className={`${colorClass} pl-2 pr-1 py-0.5 gap-1`}>
            {v}
            <button
              type="button"
              onClick={() => remove(i)}
              className="hover:text-red-400 transition-colors"
              data-testid={`${testid}-remove-${i}`}
              aria-label={`Remove ${v}`}
            >
              <X className="w-3 h-3" />
            </button>
          </Badge>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className="flex-1 bg-zinc-950 border border-zinc-800 rounded px-3 py-1.5 text-sm text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-cyan-600"
          data-testid={`${testid}-input`}
        />
        <Button type="button" size="sm" variant="outline" onClick={add} data-testid={`${testid}-add`}>
          <Plus className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
};

const NutritionProfileEditorDialog = ({ open, onClose, athleteId, athleteName, initial, onSaved }) => {
  const { api } = useAuth();
  const { language } = useLanguage();
  const [allergies, setAllergies] = useState([]);
  const [intolerances, setIntolerances] = useState([]);
  const [preferences, setPreferences] = useState([]);
  const [medicalFlags, setMedicalFlags] = useState([]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && initial) {
      setAllergies(initial.allergies || []);
      setIntolerances(initial.intolerances || []);
      setPreferences(initial.dietary_preferences || []);
      setMedicalFlags(initial.medical_flags || []);
      setNotes(initial.nutrition_notes || '');
      setError(null);
    }
  }, [open, initial]);

  if (!open) return null;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await api.put(`/nutrition/profile/${athleteId}`, {
        allergies,
        intolerances,
        dietary_preferences: preferences,
        nutrition_notes: notes.trim() || null,
        medical_flags: medicalFlags,
      });
      onSaved && onSaved(res.data);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || (language === 'IT' ? 'Salvataggio fallito' : 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="profile-editor-dialog">
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
        <header className="sticky top-0 bg-zinc-900 border-b border-zinc-800 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg font-semibold text-zinc-100">
              {language === 'IT' ? 'Profilo nutrizionale' : 'Nutrition profile'}
            </h2>
            <p className="text-xs text-zinc-500 mt-0.5">{athleteName}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="profile-editor-close">
            <X className="w-4 h-4" />
          </Button>
        </header>

        <div className="p-6 space-y-5">
          <ChipList
            label={language === 'IT' ? 'Allergie' : 'Allergies'}
            values={allergies}
            onChange={setAllergies}
            placeholder={language === 'IT' ? 'Es. arachidi, latticini…' : 'e.g. peanuts, dairy…'}
            testid="profile-allergies"
            colorClass="bg-red-500/20 text-red-300 border-red-500/40"
          />
          <ChipList
            label={language === 'IT' ? 'Intolleranze' : 'Intolerances'}
            values={intolerances}
            onChange={setIntolerances}
            placeholder={language === 'IT' ? 'Es. lattosio, glutine…' : 'e.g. lactose, gluten…'}
            testid="profile-intolerances"
            colorClass="bg-amber-500/20 text-amber-300 border-amber-500/40"
          />
          <ChipList
            label={language === 'IT' ? 'Preferenze alimentari' : 'Dietary preferences'}
            values={preferences}
            onChange={setPreferences}
            placeholder={language === 'IT' ? 'Es. vegetariano, halal…' : 'e.g. vegetarian, halal…'}
            testid="profile-preferences"
            colorClass="bg-zinc-700/60 text-zinc-200 border-zinc-600"
          />
          <ChipList
            label={language === 'IT' ? 'Condizioni mediche' : 'Medical flags'}
            values={medicalFlags}
            onChange={setMedicalFlags}
            placeholder={language === 'IT' ? 'Es. diabete, ipertensione…' : 'e.g. diabetes, hypertension…'}
            testid="profile-medical-flags"
            colorClass="bg-purple-500/20 text-purple-300 border-purple-500/40"
          />

          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-400 mb-1.5 block">
              {language === 'IT' ? 'Note nutrizionali' : 'Nutrition notes'}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder={language === 'IT' ? 'Note libere visibili allo staff…' : 'Free-text notes visible to staff…'}
              className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-cyan-600 resize-none"
              data-testid="profile-notes-textarea"
            />
          </div>

          {error && (
            <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded px-3 py-2" data-testid="profile-editor-error">
              {error}
            </div>
          )}
        </div>

        <footer className="sticky bottom-0 bg-zinc-900 border-t border-zinc-800 px-6 py-3 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="profile-editor-cancel">
            {language === 'IT' ? 'Annulla' : 'Cancel'}
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="profile-editor-save">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {language === 'IT' ? 'Salva' : 'Save'}
          </Button>
        </footer>
      </div>
    </div>
  );
};

export default NutritionProfileEditorDialog;
